// MonacoCompletionProviders.js

import {
    isInsideEjsExpression,
    formatPropertyForDisplay,
    createEjsTagSuggestions,
    htmlTags,
    jsKeywords
} from './MonacoLanguageUtils';

// Flag to track if language providers have been registered
let providersInitialized = false;

// Track registered providers by type to enable targeted cleanup
const providerRegistry = {
    tagProviders: [],
    htmlProviders: [],
    jsProviders: [],
    keywordProviders: [],
    propertyProviders: [],
    filterProviders: []
};

/**
 * Helper function to clean up specific provider types
 */
const cleanupProviderType = (type) => {
    if (providerRegistry[type] && providerRegistry[type].length > 0) {
        console.log(`Cleaning up ${providerRegistry[type].length} ${type}`);
        providerRegistry[type].forEach(d => {
            try { d.dispose(); } catch(e) {}
        });
        providerRegistry[type] = [];
    }
};

/**
 * Register completion providers for EJS templates
 * @param {object} monaco - Monaco editor instance
 * @param {object} editor - Editor instance
 * @param {object} options - Options including sampleDataRef and formatPropertyDetail
 * @returns {Array} - Disposable objects for the registered providers
 */
export const registerEjsCompletionProviders = (monaco, editor, options) => {
    const { sampleDataRef, formatPropertyDetail } = options;

    // First, clean up existing providers by type
    // This allows us to be selective about which ones we replace
    cleanupProviderType('tagProviders');
    cleanupProviderType('htmlProviders');
    cleanupProviderType('jsProviders');
    cleanupProviderType('keywordProviders');
    cleanupProviderType('propertyProviders');
    cleanupProviderType('filterProviders');

    // IMPORTANT: Only dispose providers, don't touch tokenizers/syntax highlighting
    // Set initialized flag to avoid disrupting syntax highlighting
    providersInitialized = true;

    // CRITICAL: Disable Monaco's built-in suggestions to prevent unwanted word-based suggestions
    if (editor) {
        editor.updateOptions({
            wordBasedSuggestions: false,
            quickSuggestions: { other: true, comments: true, strings: true },
            suggest: {
                showWords: false,
                snippetsPreventQuickSuggestions: false,
                filterGraceful: true
            }
        });
    }

    const disposables = [];

    // 1. EJS tag snippet provider
    const tagProvider = monaco.languages.registerCompletionItemProvider(
        ['html-ejs', 'text-ejs'],
        {
            triggerCharacters: ['<'],
            provideCompletionItems: (model, position) => {
                const lineContent = model.getLineContent(position.lineNumber);
                if (!lineContent.endsWith('<')) {
                    return { suggestions: [] };
                }
                return { suggestions: createEjsTagSuggestions(monaco) };
            }
        }
    );
    disposables.push(tagProvider);
    providerRegistry.tagProviders.push(tagProvider);

    // 2. HTML completion provider (only when not in an EJS block)
    const htmlProvider = monaco.languages.registerCompletionItemProvider(
        ['html-ejs'],
        {
            triggerCharacters: ['<', '/', '.', ' ', ':', '@', '"', "'"],
            provideCompletionItems: (model, position) => {
                try {
                    const lineContent = model.getLineContent(position.lineNumber);
                    const ejsContext = isInsideEjsExpression(lineContent, position.column - 1);
                    if (ejsContext.inside) {
                        return { suggestions: [] };
                    }
                    const suggestions = [];

                    // HTML opening tag suggestions
                    if (
                        lineContent.charAt(position.column - 2) === '<' &&
                        lineContent.charAt(position.column - 1) !== '%' &&
                        lineContent.charAt(position.column - 1) !== '/'
                    ) {
                        const wordUntilPosition = model.getWordUntilPosition(position);
                        const range = {
                            startLineNumber: position.lineNumber,
                            startColumn: wordUntilPosition.startColumn - 1,
                            endLineNumber: position.lineNumber,
                            endColumn: wordUntilPosition.endColumn
                        };
                        htmlTags.forEach((tagInfo, index) => {
                            const tagSnippet =
                                tagInfo.snippet ||
                                `${tagInfo.tag}>\${0}</${tagInfo.tag}>`;
                            suggestions.push({
                                label: tagInfo.tag,
                                kind: monaco.languages.CompletionItemKind.Snippet,
                                detail: tagInfo.detail,
                                insertText: tagSnippet,
                                insertTextRules:
                                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                sortText: `100${index}`,
                                range: range
                            });
                        });
                    }

                    // HTML closing tag suggestions
                    if (lineContent.endsWith('</')) {
                        const textBeforeCursor = model.getValueInRange({
                            startLineNumber: 1,
                            startColumn: 1,
                            endLineNumber: position.lineNumber,
                            endColumn: position.column - 1
                        });
                        const openTagRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
                        const closeTagRegex = /<\/([a-zA-Z][a-zA-Z0-9]*)>/g;
                        let match;
                        let openTags = [];
                        while ((match = openTagRegex.exec(textBeforeCursor)) !== null) {
                            if (!match[0].endsWith('/>')) {
                                openTags.push(match[1]);
                            }
                        }
                        while ((match = closeTagRegex.exec(textBeforeCursor)) !== null) {
                            const index = openTags.lastIndexOf(match[1]);
                            if (index !== -1) {
                                openTags.splice(index, 1);
                            }
                        }
                        if (openTags.length > 0) {
                            const tagToClose = openTags[openTags.length - 1];
                            suggestions.push({
                                label: tagToClose,
                                kind: monaco.languages.CompletionItemKind.Snippet,
                                detail: `Close ${tagToClose} tag`,
                                insertText: `${tagToClose}>`,
                                sortText: '001',
                                range: {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column
                                }
                            });
                        }
                    }

                    // HTML attribute suggestions
                    const tagMatch = /<([a-zA-Z][a-zA-Z0-9]*)\s[^>]*$/i.exec(lineContent);
                    if (tagMatch) {
                        const tag = tagMatch[1].toLowerCase();
                        const commonAttributes = [
                            { attr: 'id', detail: 'Unique identifier' },
                            { attr: 'class', detail: 'CSS class names' },
                            { attr: 'style', detail: 'Inline CSS styles' },
                            { attr: 'title', detail: 'Advisory information' }
                        ];

                        // Add tag-specific attributes
                        if (tag === 'a') {
                            commonAttributes.push({ attr: 'href', detail: 'URL for the link' });
                            commonAttributes.push({ attr: 'target', detail: 'Where to open the link' });
                        } else if (tag === 'img') {
                            commonAttributes.push({ attr: 'src', detail: 'Image URL' });
                            commonAttributes.push({ attr: 'alt', detail: 'Alternative text' });
                            commonAttributes.push({ attr: 'width', detail: 'Image width' });
                            commonAttributes.push({ attr: 'height', detail: 'Image height' });
                        } else if (tag === 'input') {
                            commonAttributes.push({ attr: 'type', detail: 'Input type' });
                            commonAttributes.push({ attr: 'name', detail: 'Input name' });
                            commonAttributes.push({ attr: 'value', detail: 'Input value' });
                            commonAttributes.push({ attr: 'placeholder', detail: 'Placeholder text' });
                        }

                        const wordUntilPosition = model.getWordUntilPosition(position);
                        const range = {
                            startLineNumber: position.lineNumber,
                            startColumn: wordUntilPosition.startColumn,
                            endLineNumber: position.lineNumber,
                            endColumn: wordUntilPosition.endColumn
                        };

                        commonAttributes.forEach((attrInfo, index) => {
                            suggestions.push({
                                label: attrInfo.attr,
                                kind: monaco.languages.CompletionItemKind.Property,
                                detail: attrInfo.detail,
                                insertText: `${attrInfo.attr}="\${1}"`,
                                insertTextRules:
                                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                sortText: `200${index}`,
                                range: range
                            });
                        });
                    }

                    return { suggestions };
                } catch (error) {
                    console.error("Error in HTML completion provider:", error);
                    return { suggestions: [] };
                }
            }
        }
    );
    disposables.push(htmlProvider);
    providerRegistry.htmlProviders.push(htmlProvider);

    // 3. JavaScript completion provider for record and workflow objects
    const jsProvider = monaco.languages.registerCompletionItemProvider(
        ['html-ejs', 'text-ejs'],
        {
            triggerCharacters: ['.', ' ', '(', '=', ',', "'", '"', '{', '['],
            provideCompletionItems: (model, position) => {
                try {
                    const lineText = model.getLineContent(position.lineNumber);
                    const ejsContext = isInsideEjsExpression(lineText, position.column - 1);

                    // Only provide suggestions inside EJS tags
                    if (!ejsContext.inside) {
                        return { suggestions: [] };
                    }

                    // Make sure we're in an expression or scriptlet
                    if (!ejsContext.isExpression && !ejsContext.isScriptlet) {
                        return { suggestions: [] };
                    }

                    const wordUntilPosition = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    // Get text from the start of EJS expression to cursor
                    const textBeforeCursor = lineText.substring(ejsContext.expressionStart, position.column);
                    const trimmedText = textBeforeCursor.trimEnd();
                    const suggestions = [];

                    // Provide record field suggestions after a dot
                    if (trimmedText.endsWith('.')) {
                        // Check if cursor is after "record."
                        if (trimmedText.endsWith('record.') && sampleDataRef && sampleDataRef.current) {
                            // Create a Set to track unique property names and prevent duplicates
                            const propertySet = new Set();

                            Object.keys(sampleDataRef.current).forEach((key, index) => {
                                try {
                                    // Only add this property if it's not already in our set
                                    if (!propertySet.has(key)) {
                                        propertySet.add(key);

                                        const value = sampleDataRef.current[key];
                                        suggestions.push({
                                            label: key,
                                            kind: monaco.languages.CompletionItemKind.Field,
                                            detail: formatPropertyForDisplay(value, formatPropertyDetail),
                                            insertText: key,
                                            sortText: `000${index}`,
                                            range
                                        });
                                    }
                                } catch (e) {
                                    console.error("Error processing record property:", e);
                                }
                            });
                        }

                        // Check if cursor is after "workflow."
                        if (trimmedText.endsWith('workflow.')) {
                            suggestions.push({
                                label: 'id',
                                kind: monaco.languages.CompletionItemKind.Field,
                                detail: 'Workflow identifier',
                                insertText: 'id',
                                range
                            });
                        }

                        // Check for nested properties (record.field.subfield.)
                        const nestedMatch = /\brecord\.([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\.$/i.exec(trimmedText);
                        if (nestedMatch && sampleDataRef && sampleDataRef.current) {
                            try {
                                const path = nestedMatch[1].split('.');
                                let obj = sampleDataRef.current;
                                let validPath = true;

                                // Navigate to the nested object
                                for (const part of path) {
                                    if (!obj || typeof obj !== 'object' || !(part in obj)) {
                                        validPath = false;
                                        break;
                                    }
                                    obj = obj[part];
                                }

                                // Add suggestions for all properties in the nested object
                                if (validPath && obj && typeof obj === 'object') {
                                    // Create a Set to track unique property names
                                    const propertySet = new Set();

                                    Object.keys(obj).forEach((key, index) => {
                                        // Only add if not already in the set
                                        if (!propertySet.has(key)) {
                                            propertySet.add(key);

                                            const value = obj[key];
                                            suggestions.push({
                                                label: key,
                                                kind: monaco.languages.CompletionItemKind.Field,
                                                detail: formatPropertyForDisplay(value, formatPropertyDetail),
                                                insertText: key,
                                                sortText: `000${index}`,
                                                range
                                            });
                                        }
                                    });
                                }
                            } catch (e) {
                                console.error("Error processing nested property:", e);
                            }
                        }
                    }
                    // Suggest record/workflow as top-level objects in appropriate contexts
                    else if (!textBeforeCursor.trim() || /[\s\(\{\[,;=+\-*\/><&|%!~\^]$/.test(trimmedText)) {
                        suggestions.push({
                            label: 'record',
                            kind: monaco.languages.CompletionItemKind.Variable,
                            detail: 'Current sample record',
                            insertText: 'record',
                            sortText: '010',
                            range
                        });
                        suggestions.push({
                            label: 'workflow',
                            kind: monaco.languages.CompletionItemKind.Variable,
                            detail: 'Current workflow context',
                            insertText: 'workflow',
                            sortText: '011',
                            range
                        });
                    }

                    return { suggestions };
                } catch (error) {
                    console.error("Error in JS completion provider:", error);
                    return { suggestions: [] };
                }
            }
        }
    );
    disposables.push(jsProvider);
    providerRegistry.jsProviders.push(jsProvider);

    // 4. JavaScript keyword provider
    const keywordProvider = monaco.languages.registerCompletionItemProvider(
        ['html-ejs', 'text-ejs'],
        {
            triggerCharacters: [
                'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm',
                'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'
            ],
            provideCompletionItems: (model, position) => {
                try {
                    const lineText = model.getLineContent(position.lineNumber);
                    const ejsContext = isInsideEjsExpression(lineText, position.column - 1);

                    // Only provide suggestions inside EJS tags
                    if (!ejsContext.inside) {
                        return { suggestions: [] };
                    }

                    // Only in expression or scriptlet blocks
                    if (!ejsContext.isExpression && !ejsContext.isScriptlet) {
                        return { suggestions: [] };
                    }

                    const wordUntilPosition = model.getWordUntilPosition(position);
                    if (!wordUntilPosition.word) {
                        return { suggestions: [] };
                    }

                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    const suggestions = [];

                    // Use sets to prevent duplicate keyword suggestions
                    const keywordSet = new Set();

                    // Add matching JavaScript keyword suggestions
                    jsKeywords.forEach(keyword => {
                        if (keyword.startsWith(wordUntilPosition.word.toLowerCase()) &&
                            !keywordSet.has(keyword)) {
                            keywordSet.add(keyword);
                            suggestions.push({
                                label: keyword,
                                kind: monaco.languages.CompletionItemKind.Keyword,
                                insertText: keyword,
                                range
                            });
                        }
                    });

                    // Add 'record' and 'workflow' if they match the current word
                    if ('record'.startsWith(wordUntilPosition.word.toLowerCase()) &&
                        !keywordSet.has('record')) {
                        keywordSet.add('record');
                        suggestions.push({
                            label: 'record',
                            kind: monaco.languages.CompletionItemKind.Variable,
                            detail: 'Current sample record',
                            insertText: 'record',
                            sortText: '001', // Prioritize these
                            range
                        });
                    }

                    if ('workflow'.startsWith(wordUntilPosition.word.toLowerCase()) &&
                        !keywordSet.has('workflow')) {
                        keywordSet.add('workflow');
                        suggestions.push({
                            label: 'workflow',
                            kind: monaco.languages.CompletionItemKind.Variable,
                            detail: 'Current workflow context',
                            insertText: 'workflow',
                            sortText: '002', // Prioritize these
                            range
                        });
                    }

                    // Check for partial property name after "record."
                    const partialMatch = /\brecord\.([a-zA-Z0-9_]*)$/i.exec(
                        lineText.substring(0, position.column)
                    );

                    if (
                        partialMatch &&
                        sampleDataRef &&
                        sampleDataRef.current &&
                        partialMatch[1].length > 0
                    ) {
                        try {
                            const prefix = partialMatch[1].toLowerCase();
                            const partialRange = {
                                startLineNumber: position.lineNumber,
                                startColumn: position.column - prefix.length,
                                endLineNumber: position.lineNumber,
                                endColumn: position.column
                            };

                            // Create a Set to prevent duplicates
                            const propertySet = new Set();

                            // Add suggestions for record properties matching the prefix
                            Object.keys(sampleDataRef.current)
                                .filter(key => key.toLowerCase().startsWith(prefix))
                                .forEach((key, index) => {
                                    if (!propertySet.has(key)) {
                                        propertySet.add(key);

                                        const value = sampleDataRef.current[key];
                                        suggestions.push({
                                            label: key,
                                            kind: monaco.languages.CompletionItemKind.Field,
                                            detail: formatPropertyForDisplay(value, formatPropertyDetail),
                                            insertText: key,
                                            sortText: `000${index}`, // Highest priority
                                            range: partialRange
                                        });
                                    }
                                });
                        } catch (e) {
                            console.error("Error processing partial property match:", e);
                        }
                    }

                    // Check for partial property name in nested properties
                    const nestedPartialMatch = /\brecord\.([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\.([a-zA-Z0-9_]*)$/i.exec(
                        lineText.substring(0, position.column)
                    );

                    if (
                        nestedPartialMatch &&
                        sampleDataRef &&
                        sampleDataRef.current &&
                        nestedPartialMatch[2].length > 0
                    ) {
                        try {
                            const path = nestedPartialMatch[1].split('.');
                            const prefix = nestedPartialMatch[2].toLowerCase();
                            let obj = sampleDataRef.current;
                            let validPath = true;

                            // Navigate to the nested object
                            for (const part of path) {
                                if (!obj || typeof obj !== 'object' || !(part in obj)) {
                                    validPath = false;
                                    break;
                                }
                                obj = obj[part];
                            }

                            if (validPath && obj && typeof obj === 'object') {
                                const partialRange = {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column - prefix.length,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column
                                };

                                // Create a Set to track unique property names
                                const propertySet = new Set();

                                // Add suggestions for nested properties matching the prefix
                                Object.keys(obj)
                                    .filter(key => key.toLowerCase().startsWith(prefix))
                                    .forEach((key, index) => {
                                        if (!propertySet.has(key)) {
                                            propertySet.add(key);

                                            const value = obj[key];
                                            suggestions.push({
                                                label: key,
                                                kind: monaco.languages.CompletionItemKind.Field,
                                                detail: formatPropertyForDisplay(value, formatPropertyDetail),
                                                insertText: key,
                                                sortText: `000${index}`, // Highest priority
                                                range: partialRange
                                            });
                                        }
                                    });
                            }
                        } catch (e) {
                            console.error("Error processing nested partial property match:", e);
                        }
                    }

                    return { suggestions };
                } catch (err) {
                    console.error("Error in keyword completion provider:", err);
                    return { suggestions: [] };
                }
            }
        }
    );
    disposables.push(keywordProvider);
    providerRegistry.keywordProviders.push(keywordProvider);

    // 5. Additional property filter provider
    const propertyProvider = monaco.languages.registerCompletionItemProvider(
        ['html-ejs', 'text-ejs'],
        {
            provideCompletionItems: (model, position) => {
                try {
                    // Only provide autocompletion for record fields inside EJS tags
                    const lineText = model.getLineContent(position.lineNumber);
                    const ejsContext = isInsideEjsExpression(lineText, position.column - 1);

                    if (!ejsContext.inside || (!ejsContext.isExpression && !ejsContext.isScriptlet)) {
                        return { suggestions: [] };
                    }

                    const wordUntilPosition = model.getWordUntilPosition(position);
                    if (!wordUntilPosition.word || wordUntilPosition.word.length < 1) {
                        return { suggestions: [] };
                    }

                    // Check if we're in a context where record fields might be useful
                    const textBeforeCursor = lineText.substring(ejsContext.expressionStart, position.column);
                    if (!/[a-zA-Z0-9_]$/.test(textBeforeCursor)) {
                        return { suggestions: [] };
                    }

                    // Prepare range for replacement
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: wordUntilPosition.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: wordUntilPosition.endColumn
                    };

                    const suggestions = [];

                    // Check if word might be the start of "record"
                    if ('record'.startsWith(wordUntilPosition.word.toLowerCase()) &&
                        !textBeforeCursor.trim().endsWith('.')) {
                        suggestions.push({
                            label: 'record',
                            kind: monaco.languages.CompletionItemKind.Variable,
                            detail: 'Current sample record',
                            insertText: 'record',
                            sortText: '001',
                            range
                        });
                    }

                    return { suggestions };
                } catch (error) {
                    console.error("Error in property filter provider:", error);
                    return { suggestions: [] };
                }
            }
        }
    );
    disposables.push(propertyProvider);
    providerRegistry.propertyProviders.push(propertyProvider);

    return disposables;
};
